import { useContext, useCallback } from 'react';
import _ from 'underscore';
import Promise from 'bluebird';
import { useWizardService, WIZARD_EVENTS } from '@bingads-webui-react/wizard';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { ScopeContext } from '@bingads-webui-react/scope-service';
import { saveCampaign, saveAdGroup } from '@bingads-webui-campaign-react/xandr-campaign';
import { ActivityContext, ScenarioContext } from '@bingads-webui-react/hoc-with-signal';
import { PermissionsContext } from '@bingads-webui-react/hoc-with-permissions';
import { RecommendationContext } from '@bingads-webui-campaign-react/perf-max-recommendation';
import { logCopilotRecommendations } from '@bingads-webui-campaign-react/copilot-common';
import { getGlobalSelectedCampaignObjective } from '@bingads-webui-campaign-react/campaign-onboarding-v2';
import { useCopilotLoggers } from '@bingads-webui-campaign-react/copilot-loggers';
import { AdFormat } from '@bingads-webui-campaign-react/display-plus-native-ads';
import { ImageOData } from '@bingads-webui-campaign/image-odata';
import { getVideoDeal, modelConstraints, getODataConfig } from '@bingads-webui-campaign-react/xandr-video-campaign-shared-components';
import { getBiddingScheme } from './bidding-schemes';
import { BundleLocationCriterions, BundleCountries, getActualGoalFromCampaignGoal } from './constants';
import { AdsError } from './adsError';
import { useSkipAdCreationState } from './use-skip-ad-creation';
import { isAudience as isAudienceFlow, isDisplayAdsCampaign, isDealCampaign } from '../step-constants';

export const useAdsRepository = ({
  saveAds: saveAsParam, defaultCampaign, defaultAdGroup,
}) => {
  const { send, state } = useWizardService();
  const [isSkipAdCreation, setSkipAdCreation] = useSkipAdCreationState();
  const i18n = useContext(I18nContext);
  const serverContext = useContext(ServerContext);
  const permissions = useContext(PermissionsContext);
  const [recommendationContext] = useContext(RecommendationContext);
  const { scope } = useContext(ScopeContext) || {};
  const scenarioContext = useContext(ScenarioContext);
  const activityContext = useContext(ActivityContext);

  const oDataConfig = getODataConfig(serverContext);

  const imageOdata = new ImageOData(scope.values.cid, scope.values.aid, oDataConfig);

  const { logSuggestionsApplied } = useCopilotLoggers();

  const { flow } = state.context.wizardData;

  const isImageFittingEnabled = _.result(permissions.dynamic, 'ImageFittingMVP', false)
    && !_.result(permissions.dynamic, 'ImageFittingExlcudeMAE', true)
    && _.result(permissions.dynamic, 'ImageFittingUnifiedImageROI', false);

  const saveAds = useCallback((...args) => saveAsParam(...args, { isImageFittingEnabled }), [isImageFittingEnabled, saveAsParam]);

  const load = useCallback(() => new Promise((resolve) => {
    if (state.context.wizardData.ads) {
      const isAudience = isAudienceFlow(flow);

      if (!isAudience || (isAudience && !permissions.dynamic.DisplayPlusMultipleAds)) {
        resolve(state.context.wizardData.ads.ads);
      } else {
        const { editingAdId } = state.context.wizardData;

        if (!editingAdId) {
          const ids = _.keys(state.context.wizardData.ads.ads);

          if (ids.length > 0) {
            resolve(state.context.wizardData.ads.ads[ids[0]]);
          } else {
            resolve({
              data: [],
              isValid: false,
              isDirty: false,
              currentPageIndex: 1,
            });
          }
        } else {
          resolve(state.context.wizardData.ads.ads[editingAdId] || {});
        }
      }
    } else {
      const isDealFlow = isDealCampaign(flow);
      if (isDealFlow) {
        const deal = getVideoDeal();
        if (deal) {
          send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { deal });
        }
      }

      resolve({
        data: [],
        isValid: false,
        isDirty: false,
        currentPageIndex: 1,
      });
    }
  }), [flow, permissions.dynamic.DisplayPlusMultipleAds, send, state.context.wizardData]);

  const createSaveCampaignPromise = useCallback(() => {
    if (state.context.wizardData.campaign) {
      return new Promise((resolve) => {
        resolve({ campaign: { data: state.context.wizardData.campaign } });
      });
    }

    return getBiddingScheme(scope.values.cid, scope.values.aid).then((biddingScheme) => {
      if (biddingScheme) {
        // eslint-disable-next-line no-param-reassign
        defaultCampaign.data.BiddingScheme.Type = biddingScheme;
      }

      if (permissions.IsAmplifyEnabled && permissions.IsBoostPassCampaignGoalEnabled && getGlobalSelectedCampaignObjective()) {
        const objectiveType = getActualGoalFromCampaignGoal(getGlobalSelectedCampaignObjective());
        // eslint-disable-next-line no-param-reassign
        defaultCampaign.data.CampaignObjectiveSetting = {
          CampaignObjectiveSettingType: objectiveType,
        };
      }

      return saveCampaign({
        campaign: defaultCampaign,
      }, {
        scenario: scenarioContext.name,
        activityLogger: scenarioContext.createActivity('saveDefaultCampaign'),
        i18n,
      });
    });
  }, [defaultCampaign, i18n, scenarioContext, state.context.wizardData.campaign, scope.values.cid, scope.values.aid, permissions.IsAmplifyEnabled, permissions.IsBoostPassCampaignGoalEnabled]);

  const createSaveAdGroupPromise = useCallback(saveCampaignPromise => saveCampaignPromise.then((campaignData) => {
    if (state.context.wizardData.adGroup) {
      return new Promise((resolve) => {
        resolve({ adGroup: { data: state.context.wizardData.adGroup } });
      });
    }

    const campaignMtModel = campaignData.campaign.data;
    send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { campaign: campaignMtModel, isDefaultCampaignStepModel: true });

    let adGroupModelWithConstraintsPromise = Promise.resolve(defaultAdGroup.data);

    if (isDealCampaign(flow) && defaultAdGroup.data.deal) {
      adGroupModelWithConstraintsPromise = modelConstraints(defaultAdGroup.data.deal, defaultAdGroup.data, serverContext);
    }

    return adGroupModelWithConstraintsPromise
      .then(constraintAdGroupModel => saveAdGroup({
        adGroup: { data: constraintAdGroupModel },
        campaign: campaignMtModel,
        BundleLocationCriterions,
        BundleCountries,
        isDisplayPlus: true,
        flow: state.context.wizardData.flow,
      }, {
        scenario: scenarioContext.name,
        activityLogger: scenarioContext.createActivity('saveDefaultAdGroup'),
        i18n,
        serverContext,
      }));
  }), [defaultAdGroup, flow, i18n, scenarioContext, send, serverContext, state.context.wizardData.adGroup, state.context.wizardData.flow]);

  const createAds = useCallback(
    (saveCampaignPromise, saveAdGroupPromise, adsData) => Promise.all([saveCampaignPromise, saveAdGroupPromise])
      .then(([campaignData, adGroupData]) => {
        const campaignMtModel = campaignData.campaign.data;
        const adGroupMtModel = adGroupData.adGroup.data;
        send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { adGroup: adGroupMtModel, isDefaultAdGroupStepModel: true });

        if (isSkipAdCreation) {
          return Promise.resolve().then(() => {
            if (activityContext) {
              activityContext.signal('onAdsSaved - skip ad save}');
              activityContext.signal(`campaignId - ${campaignMtModel.Id}`);
            }

            setSkipAdCreation(false);
          });
        }

        return saveAds({
          ads: adsData,
          campaign: campaignMtModel,
          adGroup: adGroupMtModel,
          oldAdsModel: adsData,
          activity: activityContext,
          imageOdata,
          permissions,
        }, {
          scenario: scenarioContext.name,
          activityLogger: scenarioContext.createActivity('saveAds'),
          i18n,
          serverContext,
        }).then((adData) => {
          const isAudience = isAudienceFlow(state.context.wizardData.flow);
          const isDisplayAdsCampaignFlow = isDisplayAdsCampaign(state.context.wizardData.flow);
          const isMultiAdFormatEnabled = permissions.dynamic.MultiAdFormat;

          if (isAudience && permissions.dynamic.DisplayPlusMultipleAds) {
            const { ads: createdAd } = adData;

            const savedAds = state.context.wizardData.ads && state.context.wizardData.ads.ads;
            const updatedAds = {
              serverErrors: [],
              systemErrors: [],
              ads: { ...savedAds },
            };

            if (!isMultiAdFormatEnabled) {
              updatedAds.ads[createdAd.data.Id] = { ...createdAd };
            } else if (adsData.adSubType === AdFormat.Native) {
              updatedAds.ads[createdAd.data.Id] = { ...createdAd, adSubType: adsData.adSubType };
            } else {
              _.forEach(createdAd.data, (ad) => {
                updatedAds.ads[ad.Id] = { ...createdAd, adSubType: adsData.adSubType, data: [ad] };
              });
            }

            send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { ads: updatedAds });
          } else {
            send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { ads: adData });
          }

          if (isAudience && permissions.dynamic.DisplayPlusCopilotRecommendations
            && (!isMultiAdFormatEnabled || (isMultiAdFormatEnabled && adsData.adSubType === AdFormat.Native))
          ) {
            logCopilotRecommendations({
              recommendationContext,
              assetGroup: adData.ads.data,
              campaignId: campaignMtModel.Id,
            });
          }

          if ((isDisplayAdsCampaignFlow && permissions.dynamic.DisplayPlusCopilotAssetGeneration) ||
            (isMultiAdFormatEnabled && adsData.adSubType === AdFormat.Display)
          ) {
            // get total copilot ads
            const totalCopilotAds = _.filter(adsData.data, adD => adD.data.creativeData);
            const suggestionIds = totalCopilotAds.map(ad => ad.data.creativeData.creativeMeta.recommendationId);

            logSuggestionsApplied(
              campaignMtModel.Id,
              adGroupMtModel.Id,
              undefined /* textSuggestions */,
              suggestionIds
            );

            if (activityContext) {
              activityContext.signal(`[DisplayAdsCopilot] - Saved copilot ads: ${totalCopilotAds.length}`);
            }
          }
          if (activityContext) {
            activityContext.signal(`onAdsSaved - ${JSON.stringify(adData)}`);
            activityContext.signal(`campaignId - ${campaignMtModel.Id}`);
          }
          return adData;
        });
      })
      // eslint-disable-next-line prefer-promise-reject-errors
      .catch(error => Promise.reject({ error: { errors: error.serverErrors } })),
    [send, isSkipAdCreation, saveAds, activityContext, scenarioContext, i18n, setSkipAdCreation, state.context.wizardData.flow, state.context.wizardData.ads, permissions.dynamic.DisplayPlusMultipleAds, permissions.dynamic.DisplayPlusCopilotRecommendations, permissions.dynamic.MultiAdFormat, permissions.dynamic.DisplayPlusCopilotAssetGeneration, recommendationContext, logSuggestionsApplied]
  );

  const save = useCallback(({ data }) => {
    if (_.isEmpty(data.data) && !isSkipAdCreation) {
      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({
        error: {
          errors: [new AdsError(i18n.getString(_TL_('Please create an ad')), 'Ad', 'AdCreationMustCreateAtLeastOneAd')],
        },
      });
    }

    const saveCampaignPromise = createSaveCampaignPromise();

    const saveAdGroupPromise = createSaveAdGroupPromise(saveCampaignPromise);

    return createAds(saveCampaignPromise, saveAdGroupPromise, data);
  }, [createAds, createSaveAdGroupPromise, createSaveCampaignPromise, i18n, isSkipAdCreation]);

  return {
    load,
    save,
  };
};
